/**
 * THE PORTFOLIO READ, TURNED THE OTHER WAY — by the person, not the project.
 *
 * The roll-up (src/portfolio.ts) answers the first question a weekly meeting
 * asks: where is each project, what is blocking it, is it on budget, when does
 * it land. This module answers the SECOND question — who is accountable, and
 * what are they doing — by reading the very same tenant-wide cards, grouped by
 * the person a card is assigned to instead of the board it sits on.
 *
 * A card carries exactly ONE assignee on a board, so each card maps to exactly
 * one person — the by-person view is a clean partition, not an estimate. Three
 * rules keep it honest:
 *
 *   · A card nobody set is NOT silently dropped. On a one-person board it is
 *     ATTRIBUTED to that sole member (the creator does the work there), flagged
 *     so the view can say the attribution was inferred, not typed. Anywhere else
 *     it lands in the Unassigned bucket, which the view surfaces on purpose —
 *     unowned work is an accountability gap, not a blank to hide.
 *   · A person's health reads from their oldest open blocker, the same
 *     thresholds the project blocker-signal uses, so a person and their
 *     projects never disagree about what "stuck" means.
 *   · "What they're doing" is the cards actually in motion (in progress, then in
 *     review); "what they're waiting on" is their own blocked cards, oldest
 *     first. A person carrying open work with nothing in motion reads plainly
 *     from those two lists.
 *
 * Pure, no store/React imports — same as portfolio.ts — so it runs on plain
 * fixtures and once over the tenant's cards in a loop.
 */
import type { Issue, IssueStatus, IssueSize } from './types';
import type { Project } from './projects';
import { reallyBlocked, blockerOf } from './blocked';
import { ageInDays, isWorking, workingAges, agingThreshold } from './card-age';
import { workingDaysInclusive, DEFAULT_CALENDAR, type WorkCalendar } from './work-calendar';
import { windowForSize, DEFAULT_IDLE_WINDOWS, type IdleWindows } from './idle-nudge';

/** One of a person's cards, with the project it belongs to named so the view can
 *  show it without another lookup. */
export interface PersonCard {
  /** The card's id — so a line can open its detail in place (the lean panel). */
  id: string;
  ref: string;
  title: string;
  status: IssueStatus;
  projectId: string;
  projectName: string;
  blocked: boolean;
  /** The blocker in a few words, when the card is blocked. */
  blockerLabel?: string;
  /** How long it has waited, in days — null when the block start was never stamped. */
  blockerDays: number | null;
  /** The due day, YYYY-MM-DD, when set — shown on the line so a card is more than a title. */
  dueDay?: string;
  /** Days past due when the card is open and overdue; 0 otherwise. */
  overdueDays: number;
}

/** One person as the accountability view reads them — every figure derived. */
export interface PersonRow {
  /** The assignee key (an email), or '' for the Unassigned bucket. */
  who: string;
  /** The shown name — the email resolved to a person, or 'Unassigned'. */
  name: string;
  isUnassigned: boolean;
  /** True when SOME of these cards were attributed by the one-person-board rule
   *  rather than set by hand — the view says so rather than imply a choice. */
  attributed: boolean;
  /** The distinct projects this person has a card on, in name order. */
  boards: { id: string; name: string }[];
  total: number;
  /** Not done, not won't-do — the work still on them. */
  open: number;
  inProgress: number;
  inReview: number;
  todo: number;
  done: number;
  /** How many of their open cards are blocked. */
  blocked: number;
  /** How much they're actively carrying — in progress + in review, less blocked
   *  (R-5a, #436). The number the overload flag and its capacity meter read. */
  inFlight: number;
  /** The overload band for their in-flight count against the cap. */
  overload: Overload;
  /** The cap the band was read against — so the capacity meter draws the right
   *  number of cells and names the cap, however an admin has tuned it. */
  overloadCap: number;
  /** The oldest open blocker's age in days — null when none is blocked/stamped. */
  oldestBlockedDays: number | null;
  /** What they're doing now — in-progress cards first, then in-review. */
  active: PersonCard[];
  /** What they're waiting on — their blocked cards, oldest first. */
  waiting: PersonCard[];
  /** Every OPEN card on this row (todo/in-progress/in-review), in board order —
   *  so the Unassigned bucket can name each unowned card to be claimed, not just
   *  the ones already in motion or blocked. */
  openCards: PersonCard[];
  /** Their headline, from the oldest open blocker (none/<7d green, >7 amber,
   *  >14 red) — the same scale the project blocker-signal uses. */
  health: 'green' | 'amber' | 'red';
  /** How many cards this person CLOSED in the last 7 days — credited to whoever
   *  made the move (the activity log's `who`), not the card's current assignee,
   *  so it reads as "what they did", not "what landed on them". */
  closedThisWeek: number;
  /** Their oldest in-flight card and its age against its board's own norm — the
   *  "how long has their work been sitting" read. Null when nothing of theirs is
   *  in flight (or no start was stamped). */
  oldestStuck: { ref: string; days: number; aged: boolean } | null;
  /** Their forward load over the next few working weeks (0216 forward capacity) —
   *  "how loaded, and which week do they tip over". Null when they have no
   *  due-dated open work in the window, so the view shows the bars only when there
   *  is something to weigh. */
  capacity: PersonCapacity | null;
}

/** The whole by-person read: the people, and the unowned work set apart. */
export interface Accountability {
  /** Real assignees, most-stuck first, then most open work, then by name. */
  people: PersonRow[];
  /** The cards nobody owns (and that no one-person-board rule could attribute).
   *  Null when every card has an owner. Surfaced by the view, never buried. */
  unassigned: PersonRow | null;
}

/** An in-progress or in-review card is "in motion"; a done or won't-do card is
 *  off the person's plate; to-do is open but not yet moving. The BACKLOG is the
 *  holding pen — candidate work nobody has committed to yet — so it is NOT open
 *  work and NOT load: it never counts toward a person's "open" tally, their
 *  overdue, or the unowned-work gap (the aggregate-bucket invariant — backlog is
 *  candidate, never committed; the forward-load bars already exclude it below). */
const isOpen = (s: IssueStatus): boolean => s !== 'done' && s !== 'wontdo' && s !== 'backlog';

const DAY_MS = 86_400_000;

/** The day a `YYYY-MM-DD` falls on, as UTC midnight ms — for an overdue diff. */
function dayMs(iso: string): number | null {
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return null;
  return Date.UTC(y, m - 1, d);
}

/** A running tally for one person while the cards are walked. */
interface Bucket {
  who: string;
  attributed: boolean;
  boards: Map<string, string>;
  total: number;
  open: number;
  inProgress: number;
  inReview: number;
  todo: number;
  done: number;
  active: PersonCard[];
  waiting: PersonCard[];
  openCards: PersonCard[];
  /** Their oldest in-flight card so far, and its age against its board's norm. */
  stuck: { ref: string; days: number; aged: boolean } | null;
}

const emptyBucket = (who: string): Bucket => ({
  who,
  attributed: false,
  boards: new Map(),
  total: 0,
  open: 0,
  inProgress: 0,
  inReview: 0,
  todo: 0,
  done: 0,
  active: [],
  waiting: [],
  openCards: [],
  stuck: null,
});

const RANK: Record<'green' | 'amber' | 'red', number> = { green: 0, amber: 1, red: 2 };

/** A blocker age → the person's grade, on the project blocker-signal's scale. */
function healthFromOldest(oldest: number | null): 'green' | 'amber' | 'red' {
  if (oldest == null) return 'green';
  if (oldest > 14) return 'red';
  if (oldest > 7) return 'amber';
  return 'green';
}

/**
 * THE OVERLOAD RULE — R-5a (#436), the "are they carrying too much at once?" read,
 * derived from work already on the row: no timesheets, no new capture.
 *
 * In-flight is what a person is actively holding — IN PROGRESS + IN REVIEW, minus
 * what is BLOCKED (a blocked card is waiting on someone else, not weighing on them
 * right now). Measured against a fixed cap (default 5, an admin tunes it): OVER the
 * cap is Overloaded (the loud signal), the cap and just under it is Busy (a calm
 * early one), and below that carries no flag. A fixed cap — not a team-relative one
 * — because it is predictable and explainable ("more than five things on the go"),
 * and surfaces a whole team that is uniformly over the line, which a relative rule
 * would hide. This is distinct from the health pill (blockers = "is it stuck?").
 */
export const OVERLOAD_CAP_DEFAULT = 5;
export type Overload = 'none' | 'busy' | 'overloaded';
/** How much a person is actively carrying — in progress + in review, less blocked
 *  (never below zero). The number the overload flag and its capacity meter read. */
export function inFlightCount(inProgress: number, inReview: number, blocked: number): number {
  return Math.max(0, inProgress + inReview - blocked);
}
/** The overload band for an in-flight count against a cap: over the cap is
 *  overloaded, the cap or one below it is busy, anything less carries no flag. */
export function overloadLevel(inFlight: number, cap: number = OVERLOAD_CAP_DEFAULT): Overload {
  if (cap <= 0) return 'none';
  if (inFlight > cap) return 'overloaded';
  if (inFlight >= cap - 1) return 'busy';
  return 'none';
}

/** Turn a finished bucket into the row the view reads. `closedThisWeek` is the
 *  count of closes this person MADE in the window (credited by mover), tallied
 *  once across the tenant and handed in. */
function toRow(b: Bucket, resolveName: (who: string) => string, closedThisWeek: number, cap: number): PersonRow {
  const oldestBlockedDays = b.waiting.reduce<number | null>(
    (max, c) => (c.blockerDays != null && (max == null || c.blockerDays > max) ? c.blockerDays : max),
    null,
  );
  // A blocked card counts against in-flight ONLY when it is itself in an in-flight
  // lane (in progress / staging / in review). A card blocked while still in To-Do or
  // the backlog was never in motion, so subtracting the FULL blocked count understated
  // what they are carrying and, near the cap, could mis-band (R-5a: "a blocked card is
  // waiting on someone else" means a blocked card IN FLIGHT). b.waiting holds every
  // blocked card regardless of status, so scope the subtraction to the in-flight lanes.
  const blockedInFlight = b.waiting.filter(
    (c) => c.status === 'inprogress' || c.status === 'staging' || c.status === 'inreview',
  ).length;
  const inFlight = inFlightCount(b.inProgress, b.inReview, blockedInFlight);
  // In motion first (in progress, then in review); then the rest of the active
  // list is already status-ordered by how it was pushed.
  const boards = Array.from(b.boards, ([id, name]) => ({ id, name })).sort((x, y) =>
    x.name.localeCompare(y.name),
  );
  const isUnassigned = b.who === '';
  return {
    who: b.who,
    name: isUnassigned ? 'Unassigned' : resolveName(b.who) || b.who,
    isUnassigned,
    attributed: b.attributed,
    boards,
    total: b.total,
    open: b.open,
    inProgress: b.inProgress,
    inReview: b.inReview,
    todo: b.todo,
    done: b.done,
    // (F6) Count blocked over the SAME population as `open` — backlog excluded. `b.waiting`
    // holds every blocked card regardless of status (it feeds the "waiting on" list and the
    // oldest-blocker age, which legitimately name parked candidates too), but the displayed
    // "N blocked" sits beside "N open", and open never counts backlog (the aggregate-bucket
    // invariant). Counting backlog-blocked here read "8 of my 27 open are blocked" when it was
    // really 3 of 27 plus 5 parked candidates.
    blocked: b.waiting.filter((c) => isOpen(c.status)).length,
    inFlight,
    overload: overloadLevel(inFlight, cap),
    overloadCap: cap,
    oldestBlockedDays,
    active: b.active,
    waiting: b.waiting.sort((x, y) => (y.blockerDays ?? 0) - (x.blockerDays ?? 0)),
    openCards: b.openCards,
    health: healthFromOldest(oldestBlockedDays),
    closedThisWeek,
    oldestStuck: b.stuck,
    capacity: null, // attached by buildAccountability once the forward load is derived
  };
}

/**
 * EVERY PERSON'S WORK across the tenant, from each board's own cards (keyed by
 * board id) and the project rows that name each board and its members. A card's
 * person is its assignee; on a one-person board an unset card is attributed to
 * the sole member; anything else with no assignee goes to Unassigned.
 */
export function buildAccountability(
  projects: Project[],
  issuesByBoard: Map<string, Issue[]>,
  opts?: { now?: number; resolveName?: (who: string) => string; overloadCap?: number; cal?: WorkCalendar },
): Accountability {
  const now = opts?.now ?? Date.now();
  const sinceMs = now - 7 * DAY_MS;
  const resolveName = opts?.resolveName ?? ((who) => who);
  const cap = opts?.overloadCap ?? OVERLOAD_CAP_DEFAULT;
  const cal = opts?.cal ?? DEFAULT_CALENDAR;
  // The owner each card is attributed to (its assignee, or a one-person board's sole
  // member), and where it lives — so the forward-load read keys on the SAME person the
  // row counts credit, and each capacity line can carry a project chip.
  const ownedIssues: Issue[] = [];
  const metaById = new Map<string, { projectId: string; projectName: string }>();
  const buckets = new Map<string, Bucket>();
  const bucketFor = (who: string): Bucket => {
    let b = buckets.get(who);
    if (!b) buckets.set(who, (b = emptyBucket(who)));
    return b;
  };

  // CLOSED THIS WEEK, CREDITED TO THE MOVER — a card reaching done/won't-do in the
  // window is counted for the person who made the move (activity.who), not the
  // card's current assignee. One pass over the transition log across the tenant.
  const closedByWho = new Map<string, number>();
  for (const issues of issuesByBoard.values()) {
    for (const i of issues) {
      for (const a of i.activity ?? []) {
        if (a.at < sinceMs || a.at > now) continue;
        if (a.toStatus !== 'done' && a.toStatus !== 'wontdo') continue;
        // A move already at a finished status (done → won't-do) is a reclassify,
        // not a fresh close — count only an arrival from an unfinished status.
        if (a.fromStatus === 'done' || a.fromStatus === 'wontdo') continue;
        const who = a.who?.trim();
        if (!who) continue;
        closedByWho.set(who, (closedByWho.get(who) ?? 0) + 1);
      }
    }
  }

  for (const project of projects) {
    const issues = issuesByBoard.get(project.id) ?? [];
    const projectName = project.name ?? 'Untitled project';
    const members = (project.members ?? []).map((m) => m.trim()).filter(Boolean);
    const soleMember = members.length === 1 ? members[0] : undefined;
    // The board's genuinely-blocked cards, so a card knows it is one without a
    // second pass — reallyBlocked is the same filter the project view trusts.
    // A finished card is off the plate: reallyBlocked is deliberately status-blind
    // (blocked.ts), so a done/won't-do card whose blocker was never cleared would
    // otherwise still count here — a person reading "2 blocked" for work that is
    // actually done (monitoring-witness, 1 Nov). Drop the settled cards first.
    const blockedIds = new Set(
      reallyBlocked(issues.filter((i) => i.status !== 'done' && i.status !== 'wontdo')).map((i) => i.id),
    );
    // The board's own "in flight too long" line, measured once so every card is
    // judged against the same norm (card-age.ts).
    const agingLine = agingThreshold(workingAges(issues, now));

    for (const issue of issues) {
      const set = issue.assignee?.trim() || '';
      // A one-person board's unset card is that member's; note it was inferred.
      const attributedHere = !set && !!soleMember;
      const who = set || (soleMember ?? '');
      const b = bucketFor(who);
      if (attributedHere) b.attributed = true;
      b.boards.set(project.id, projectName);
      b.total += 1;
      // For the forward-load read: the card tagged with the owner it is attributed to
      // (so capacity and the row's counts never disagree) and where it lives.
      if (who) {
        ownedIssues.push(issue.assignee?.trim() === who ? issue : ({ ...issue, assignee: who } as Issue));
        metaById.set(issue.id, { projectId: project.id, projectName });
      }

      const status = issue.status;
      if (status === 'done') b.done += 1;
      else if (status === 'inprogress' || status === 'staging') b.inProgress += 1;
      else if (status === 'inreview') b.inReview += 1;
      else if (status === 'todo') b.todo += 1;
      // A backlog card is candidate work — counted only in `total`, never in the
      // to-do or the open tallies (it is the holding pen, not committed work).
      if (isOpen(status)) b.open += 1;

      const blocked = blockedIds.has(issue.id);
      const dueDay = issue.dueDate || undefined;
      let overdueDays = 0;
      if (isOpen(status) && dueDay) {
        const today = Math.floor(now / DAY_MS) * DAY_MS;
        const due = dayMs(dueDay);
        if (due != null && due < today) overdueDays = Math.round((today - due) / DAY_MS);
      }
      const card: PersonCard = {
        id: issue.id,
        ref: issue.ref,
        title: issue.title,
        status,
        projectId: project.id,
        projectName,
        blocked,
        blockerLabel: blocked ? blockerOf(issue) || issue.blockerTarget?.trim() || 'something' : undefined,
        blockerDays: blocked ? ageInDays(issue.blockedAt, now) : null,
        dueDay,
        overdueDays,
      };
      // In motion — the answer to "what are they doing" — hands-on work (in
      // progress or in staging) ahead of in review, so the list reads in the
      // order work flows.
      if (status === 'inprogress' || status === 'staging') b.active.unshift(card);
      else if (status === 'inreview') b.active.push(card);
      if (blocked) b.waiting.push(card);
      // Every open card, in board order — so unowned todo cards can be named and
      // claimed, not just the ones already in motion or blocked.
      if (isOpen(status)) b.openCards.push(card);

      // Track their oldest in-flight card, aged against this board's own norm —
      // the "how long has their work been sitting" read.
      if (isWorking(issue)) {
        const days = ageInDays(issue.startedAt, now);
        if (days != null && (b.stuck == null || days > b.stuck.days)) {
          b.stuck = { ref: issue.ref, days, aged: days > agingLine };
        }
      }
    }
  }

  const unassignedBucket = buckets.get('');
  buckets.delete('');
  const people = Array.from(buckets.values())
    .map((b) => toRow(b, resolveName, closedByWho.get(b.who) ?? 0, cap))
    .sort(
      (a, b) =>
        RANK[b.health] - RANK[a.health] || b.open - a.open || a.name.localeCompare(b.name),
    );
  // An accountability gap is unowned COMMITTED work — a card someone should be
  // driving but no one owns. A card sitting unowned in the BACKLOG is not a gap:
  // the backlog is candidate work and is meant to be unowned until it is pulled
  // onto the board, so an unassigned bucket holding only backlog cards raises no
  // flag (the aggregate-bucket invariant — it would otherwise cry wolf and train
  // managers to ignore the real signal). Keyed on open, not total.
  const unassigned =
    unassignedBucket && unassignedBucket.open > 0
      ? toRow(unassignedBucket, resolveName, closedByWho.get('') ?? 0, cap)
      : null;

  // The forward load, derived once over every owned card and attached to its person —
  // "how loaded, and which week they tip over" (0216). Unowned work has no overload, so
  // the Unassigned bucket carries none.
  const capByWho = new Map<string, PersonCapacity>();
  for (const c of forwardCapacity(ownedIssues, now, cal, { resolveName, meta: (i) => metaById.get(i.id) ?? { projectId: '', projectName: '' } })) {
    capByWho.set(c.who, c);
  }
  for (const r of people) r.capacity = capByWho.get(r.who) ?? null;

  return { people, unassigned };
}

/* ────────────────────────────────────────────────────────────────────────────
 * FORWARD CAPACITY — "who is overloaded, and WHICH week" (29 Oct, the pm-witness
 * audit). The overload read above answers "is this person carrying too much RIGHT
 * NOW" (in-flight vs a cap). This answers the forward question a steering meeting
 * asks next: looking at the weeks ahead, whose committed work outruns the working
 * days they actually have — and when does it first tip over.
 *
 * It is DECISION-TIER, not a timesheet: a card's effort is read from its size
 * (quick/medium/long → a rough day cost), it lands in the week its due date falls
 * in (an overdue card presses on the current week), and it is weighed against the
 * WORKING days in that week from the workspace calendar — Mon–Fri minus any
 * holiday, the same calendar the forecast uses. No new capture, no hours logged;
 * the inputs — who owns a card, how big it is, when it is due — are already there.
 * Unowned work has no one to overload, so it is left out (the roster already
 * surfaces it as an accountability gap).
 */

/* A card's rough working-day cost REUSES the board's own size scale — the idle-nudge
 * windows (S 3 · M 5 · L 10 working days, 5 unsized by default, and per-board tunable):
 * the same sizing the rest of the app already trusts, so no second estimate is invented
 * and a board that has tuned its sizes tunes its capacity read too. {@link windowForSize}. */

export interface CapacityCard {
  /** The card's id — so a "due in this window" line can open its detail in place. */
  id: string;
  ref: string;
  title: string;
  /** The due day, YYYY-MM-DD. */
  dueDay: string;
  /** The rough working-day cost this card contributes. */
  effort: number;
  /** The card's size, for the "· M" label on the line (the effort's source). */
  size: IssueSize | '';
  /** True when the card is already past due — it presses on the current week. */
  overdue: boolean;
  /** The board this card lives on, so the line carries a project chip and opens it. */
  projectId: string;
  projectName: string;
}

/** One week of a person's forward load. */
export interface CapacityWeek {
  /** Monday of the week, YYYY-MM-DD. */
  weekStart: string;
  /** Sunday of the week, YYYY-MM-DD. */
  weekEnd: string;
  /** The size-weighted working-day cost of the cards due in (or overdue onto) this week. */
  committed: number;
  /** The working days this person actually has this week, from the calendar. */
  available: number;
  /** True when committed effort outruns the working days available. */
  over: boolean;
  /** The cards making up the load, so the drill can name them. */
  cards: CapacityCard[];
}

/** One person's forward capacity over the horizon. */
export interface PersonCapacity {
  who: string;
  name: string;
  weeks: CapacityWeek[];
  /** Size-weighted working-days of work due across the whole window. */
  committedTotal: number;
  /** Working days the person has across the whole window (calendar). */
  availableTotal: number;
  /** committedTotal / availableTotal as a percent — the headline "how loaded" figure.
   *  Forward work only; overdue debt is carried separately so it never inflates this. */
  loadPct: number;
  /** The Monday of the first week they tip over, or null when they never do in the window. */
  firstOverWeek: string | null;
  /** Size-weighted working-days of OVERDUE committed work (due before today). Kept out
   *  of the weekly bars and the loadPct — a backlog of slipped work is a different fact
   *  from forward commitment, and folding it onto the current week inflated the headline
   *  (monitoring-witness, 1 Nov). Zero when nothing is overdue. */
  overdueEffort: number;
  /** The overdue cards, soonest-due first — so the debt can be named, not just counted. */
  overdueCards: CapacityCard[];
  /** Open committed cards with NO due date — they cannot be placed on a week, so they
   *  are counted here rather than dropped. Dropping them silently let a loaded person
   *  read as idle (monitoring-witness, 1 Nov). dueDay is '' on these. */
  undatedCards: CapacityCard[];
}

/** Monday (UTC) of the week containing `ms`, as YYYY-MM-DD. */
function mondayOf(ms: number): string {
  const d = new Date(ms);
  const dow = (d.getUTCDay() + 6) % 7; // 0 = Monday
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate() - dow)).toISOString().slice(0, 10);
}
/** `YYYY-MM-DD` + n days, as `YYYY-MM-DD` (UTC). */
function addDaysISO(iso: string, n: number): string {
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

/**
 * EACH PERSON'S FORWARD LOAD, week by week, over the next `weeks` (default 4). Most
 * pressing first — whoever tips over earliest, then the heaviest total. A person with
 * no due-dated open work in the window does not appear (there is nothing to weigh).
 */
export function forwardCapacity(
  issues: Issue[],
  now: number,
  cal: WorkCalendar = DEFAULT_CALENDAR,
  opts?: {
    weeks?: number;
    resolveName?: (who: string) => string;
    windows?: IdleWindows;
    /** Where a card lives, so each "due in this window" line carries a project chip
     *  and opens it. Defaults to empty — the pure arithmetic needs no project. */
    meta?: (i: Issue) => { projectId: string; projectName: string };
  },
): PersonCapacity[] {
  const horizon = Math.max(1, opts?.weeks ?? 3);
  const resolveName = opts?.resolveName ?? ((w: string) => w);
  const windows = opts?.windows ?? DEFAULT_IDLE_WINDOWS;
  const metaOf = opts?.meta ?? (() => ({ projectId: '', projectName: '' }));
  const effortOf = (size: IssueSize | '' | undefined): number => windowForSize(size, windows);
  const weekStarts = Array.from({ length: horizon }, (_, i) => addDaysISO(mondayOf(now), i * 7));
  const lastWeekEnd = addDaysISO(weekStarts[horizon - 1], 6);
  // Today as a UTC day, the same boundary the row lists' overdueDays use, so a card
  // reads overdue consistently in "Due in this window" and in "Doing now" (it had been
  // flagged by week — due < this Monday — which left a card due earlier THIS week
  // unmarked here yet "1d over" there). Display only; week placement is unchanged.
  const todayISO = new Date(now).toISOString().slice(0, 10);

  /* The week a due day lands in: an overdue card presses on the current week (0), a
     card past the horizon is not weighed here, otherwise the latest week it is due by. */
  const weekIndexOf = (dueISO: string): number => {
    if (dueISO < weekStarts[0]) return 0;
    if (dueISO > lastWeekEnd) return -1;
    for (let i = horizon - 1; i >= 0; i--) if (dueISO >= weekStarts[i]) return i;
    return 0;
  };

  const blank = (): CapacityWeek[] =>
    weekStarts.map((ws) => {
      const weekEnd = addDaysISO(ws, 6);
      // The current week counts only the working days from TODAY onward — its earlier
      // days have already elapsed, so offering them as "available" to absorb work
      // overstated this week's room (monitoring-witness, 1 Nov). A later week always
      // starts after today, so it keeps its full calendar week. On a weekend snapshot
      // this leaves 0 available this week, which is the honest figure.
      const from = ws < todayISO ? todayISO : ws;
      return { weekStart: ws, weekEnd, committed: 0, available: workingDaysInclusive(from, weekEnd, cal), over: false, cards: [] };
    });

  interface Load { weeks: CapacityWeek[]; overdue: CapacityCard[]; undated: CapacityCard[]; }
  const byPerson = new Map<string, Load>();
  const ensure = (who: string): Load => {
    let l = byPerson.get(who);
    if (!l) { l = { weeks: blank(), overdue: [], undated: [] }; byPerson.set(who, l); }
    return l;
  };
  for (const i of issues) {
    const who = i.assignee;
    if (!who) continue; // unowned work is nobody's overload
    if (i.status === 'done' || i.status === 'wontdo') continue; // off their plate
    if (i.status === 'backlog') continue; // the holding pen — nothing here is committed (idle-nudge.ts)
    const effort = effortOf(i.size);
    const m = metaOf(i);
    const base = { id: i.id, ref: i.ref, title: i.title ?? '', effort, size: (i.size ?? '') as IssueSize | '', projectId: m.projectId, projectName: m.projectName };
    const due = i.dueDate;
    // No due date — committed but not placeable, so it is counted (named) rather than
    // dropped; a person with only this reads as loaded, not idle.
    if (!due) { ensure(who).undated.push({ ...base, dueDay: '', overdue: false }); continue; }
    // Overdue — already slipped, so it is its own figure and never folded onto a week.
    if (due < todayISO) { ensure(who).overdue.push({ ...base, dueDay: due, overdue: true }); continue; }
    const wi = weekIndexOf(due);
    if (wi < 0) continue; // beyond the horizon — not weighed
    const w = ensure(who).weeks[wi];
    w.committed = Math.round((w.committed + effort) * 100) / 100;
    w.cards.push({ ...base, dueDay: due, overdue: false });
  }

  const sum = (cards: CapacityCard[]) => Math.round(cards.reduce((n, c) => n + c.effort, 0) * 100) / 100;
  const out: PersonCapacity[] = [];
  for (const [who, { weeks, overdue, undated }] of byPerson) {
    let firstOver: string | null = null;
    let committedTotal = 0;
    let availableTotal = 0;
    for (const w of weeks) {
      w.over = w.committed > w.available;
      if (w.over && !firstOver) firstOver = w.weekStart;
      committedTotal += w.committed;
      availableTotal += w.available;
    }
    committedTotal = Math.round(committedTotal * 100) / 100;
    const loadPct = availableTotal > 0 ? Math.round((committedTotal / availableTotal) * 100) : 0;
    overdue.sort((a, b) => (a.dueDay < b.dueDay ? -1 : a.dueDay > b.dueDay ? 1 : 0));
    out.push({
      who, name: resolveName(who), weeks, committedTotal, availableTotal, loadPct, firstOverWeek: firstOver,
      overdueEffort: sum(overdue), overdueCards: overdue, undatedCards: undated,
    });
  }
  // The pressure each person is under, for ordering: a week over, then the weight of
  // forward + overdue work. Surfaces the loaded and the behind ahead of the clear.
  const pressureOf = (p: PersonCapacity) => p.committedTotal + p.overdueEffort;
  out.sort((a, b) => {
    // Whoever tips over comes before whoever never does; among those who do, the
    // earlier week first (ISO days compare lexically). Then the heaviest pressure
    // (forward + overdue), then any undated work, then name.
    if (a.firstOverWeek && b.firstOverWeek) {
      if (a.firstOverWeek !== b.firstOverWeek) return a.firstOverWeek < b.firstOverWeek ? -1 : 1;
    } else if (a.firstOverWeek) return -1;
    else if (b.firstOverWeek) return 1;
    return pressureOf(b) - pressureOf(a)
      || b.undatedCards.length - a.undatedCards.length
      || a.name.localeCompare(b.name);
  });
  return out;
}
